import {AccountNumber, Party, profileDecoder} from '@amicamutual/amica-common';
import {handle} from 'redux-pack';

import {billingPaymentPolicyType} from 'enums/billing';
import {buildAmicaApiRequest} from 'shared/APIServices/utils/authenticatedAPIRequest';
import {decodeResponse} from 'utils/decode-response';

export const LOAD_PROFILE_DATA = 'profile/LoadProfileData';
const SET_PARTY_UUID = 'profile/partyUUID';
const SET_LAST_LOGIN_DATE = 'profile/lastLoginDate';
const SET_CURRENT_LOGIN_DATE = 'profile/currentLoginDate';
const SET_ONBOARDING_COMPLETED = 'profile/SET_ONBOARDING_COMPLETED';

interface ProfileHeader {
  'amica-responsedetail': string;
}

export interface State {
  party: Party | null;
  accounts: ReadonlyArray<AccountNumber>;
  isLoadingProfile: boolean;
  errorLoadingProfile: string | null;
  currentPaymentPolicy: billingPaymentPolicyType;
  payloadHeader: ProfileHeader | null;
  partyUUID: string | undefined;
  currentLoginDate: Date | null;
  onboardingCompleted: boolean;
  lastLoginDate?: Date;
}

export const initialState: State = {
  party: null,
  accounts: [],
  isLoadingProfile: false,
  errorLoadingProfile: null,
  currentPaymentPolicy: billingPaymentPolicyType.NONE,
  payloadHeader: null,
  partyUUID: undefined,
  lastLoginDate: undefined,
  currentLoginDate: null,
  onboardingCompleted: false
};

/**
 * Action to fetch profile data.
 */
export const loadProfile = (id: string) => ({
  type: LOAD_PROFILE_DATA,
  promise: buildAmicaApiRequest({url: `/v2/party/${id}`, method: 'get'})
});

export const setOnboardingCompleted = () => ({type: SET_ONBOARDING_COMPLETED});

export const setPartyUUID = (partyUUID: string | undefined) => ({
  type: SET_PARTY_UUID,
  payload: partyUUID
});

export const setLastLoginDate = (lastLoginDate?: Date) => ({
  type: SET_LAST_LOGIN_DATE,
  payload: lastLoginDate
});

export const setCurrentLoginDate = (loginDate: Date) => ({
  type: SET_CURRENT_LOGIN_DATE,
  payload: loginDate
});

/**
 * Given a current state, perform the action provided and return a new state.
 * @param state Current state. Defaults to initial state.
 * @param action Action that was dispatched.
 * @returns A new state.
 */
const profileReducer = (state: State = initialState, action: any) => {
  const {type, payload} = action;

  switch (type) {
    case LOAD_PROFILE_DATA:
      return handle(state, action, {
        start: (prevState) => ({
          ...prevState,
          isLoadingProfile: true,
          errorLoadingProfile: null,
          errorLoadingPreferences: null,
          errorUpdatingPaperlessPreferences: null,
          successUpdatingPaperlessPreferences: false,
          successEnrollingPaperlessPreferences: false
        }),
        finish: (prevState) => ({...prevState, isLoadingProfile: false}),
        failure: (prevState) => ({...prevState, errorLoadingProfile: payload.message}),
        success: (prevState) => {
          const {party, accounts} = decodeResponse(profileDecoder, payload.data);

          return {
            ...prevState,
            party,
            accounts,

            payloadHeader: payload.headers
          };
        }
      });

    case SET_PARTY_UUID:
      return {...state, partyUUID: payload};

    case SET_LAST_LOGIN_DATE:
      return {...state, lastLoginDate: payload};

    case SET_CURRENT_LOGIN_DATE:
      return {...state, currentLoginDate: payload};

    case SET_ONBOARDING_COMPLETED:
      return {...state, onboardingCompleted: true};

    default:
      return state;
  }
};

export default profileReducer;
